﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

// An application page with centered content.

// ----------------------------------------------------------------------------------------------------
// Imports
// ----------------------------------------------------------------------------------------------------

import { applyThemeStyles } from "~base/Styling";
import * as React from "react";
import { createUseStyles, useTheme } from "react-jss";
import { JssClass } from "~base/Styling";
import { ConsilioBaseTheme } from "~base/Styling/ConsilioTheme";
import { CenterContent } from "~base/Components/Layout/CenterContent";
import { PageLoading } from "./PageLoading";
import { LoadingPinsColor } from "~base/Components/Composite/LoadingPins/Types";
import { useTransitionStyles } from "~base/Styling/Transitions/TransitionGroup";
import { useGlobalStyles } from "./GlobalStyles";
import { useServerContext } from "~base/Services/ServerContext";
import { User } from "~base/Services/API/Account/Model";
import { useAdaptiveViewport } from "~base/Utilities/AdaptiveViewport";
import { textCodesAreStored } from "~base/Services/TextCodeService";
import { ConsilioSingleTransition } from "~base/Styling/Transitions/ConsilioSingleTransition";

// ----------------------------------------------------------------------------------------------------
// Props
// ----------------------------------------------------------------------------------------------------

interface SplashPageProps {
    isLoading: boolean;
    showSplashScreen: boolean;
    splashScreen?: React.ReactNode;
    children?: React.ReactNode;
}

//-------------------------------------------------------------------------------------
// Styling
//-------------------------------------------------------------------------------------

export interface ConsilioThemePagesSharedSplashPageStyles {
    splashScreen?: JssClass;
    background?: JssClass;
    backgroundImage?: React.ReactNode;
    backgroundImageFunc?: (user?: User) => React.ReactNode;
    loadingPinsStyle?: LoadingPinsColor;
    loadingContent?: JssClass;
}

const useStyles = createUseStyles(
    (theme: ConsilioBaseTheme) => {
        return {
            background: applyThemeStyles(
                theme.components.pages.shared.splashPage.background,
                {
                    backgroundImage: (user: User) => {
                        const backgroundImageFromFunc =
                            theme.components.pages.shared.splashPage
                                .backgroundImageFunc &&
                            `url("${theme.components.pages.shared.splashPage.backgroundImageFunc(user)}")`;
                        const backgroundImage = theme.components.pages.shared
                            .splashPage.backgroundImage
                            ? `url("${theme.components.pages.shared.splashPage.backgroundImage}")`
                            : null;

                        return backgroundImageFromFunc
                            ? backgroundImageFromFunc
                            : backgroundImage;
                    },
                }
            ),
            loadingContent: {
                visibility: "hidden",
            },
        };
    },
    { name: "SplashPage" }
);

// ----------------------------------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------------------------------

const SplashPage: React.FunctionComponent<SplashPageProps> = ({
    children,
    isLoading,
    showSplashScreen,
    splashScreen,
}) => {
    const theme: ConsilioBaseTheme = useTheme();
    useGlobalStyles(theme);
    useAdaptiveViewport();
    const [fadeClasses, timeout] = useTransitionStyles("FadeIn", false);
    const userContext = useServerContext();

    const textCodesLoaded = textCodesAreStored();
    const pageIsLoading = isLoading;

    const classes = useStyles(userContext.user as unknown);

    const animationDivStyles = textCodesLoaded ? "" : classes.loadingContent;

    return (
        <>
            <CenterContent wrapperClassName={classes.background}>
                <ConsilioSingleTransition
                    active={!pageIsLoading}
                    transitionClassNames={fadeClasses}
                    timeout={timeout}
                >
                    <div className={animationDivStyles}>{children}</div>
                </ConsilioSingleTransition>
            </CenterContent>
            <CenterContent>
                {showSplashScreen && textCodesLoaded && splashScreen}
            </CenterContent>
            <PageLoading
                loading={pageIsLoading && !showSplashScreen}
                loadingPinsStyle={
                    theme.components.pages.shared.splashPage.loadingPinsStyle
                }
            />
        </>
    );
};

export default SplashPage;
export { SplashPage };
